/* eslint-disable react-refresh/only-export-components */

import { useEffect, useMemo } from "react";
import { createBrowserRouter, useNavigate } from "react-router-dom";

import { jwtDecode } from "jwt-decode";
import AuthGroup from "../configs/authgroup";
import Permissions from "../configs/permission"; // Import permission constants
import AdminLayout from "./Components/Layout/Admin";
import { CompanyBillCreatePage } from "./pages/bill/companybill/create/index.jsx";
import { CompanyBillPage } from "./pages/bill/companybill/Index.jsx";
import { CustomerBilCreatePage } from "./pages/bill/customerbill/create/index.jsx";
import { CustomerBilPage } from "./pages/bill/customerbill/Index.jsx";
import LogoutSuccess from "./pages/callback/Logout";
import Success from "./pages/callback/Success";
import { CompanyCreatePage } from "./pages/company/create/index";
import { CompanyPage } from "./pages/company/Index";
import { CustomerCreatePage } from "./pages/customer/create/index";
import { CustomerPage } from "./pages/customer/Index";
import Home from "./pages/home/Index.jsx";
import Login from "./pages/login/Index.jsx";
import Logout from "./pages/logout/Index.jsx";
import { DestinationDeliveryCreatePage } from "./pages/package/destinationdelivery/create/index.jsx";
import { DestinationDeliveryPage } from "./pages/package/destinationdelivery/Index.jsx";
import { DestinationPackageCreatePage } from "./pages/package/destinationpackage/create/index.jsx";
import { DestinationPackagePage } from "./pages/package/destinationpackage/Index.jsx";
import { DestinationPackageDeliveryCreatePage } from "./pages/package/destinationpackagedelivery/create/index.jsx";
import { DestinationPackageDeliveryPage } from "./pages/package/destinationpackagedelivery/Index.jsx";
import { OriginPackageCreatePage } from "./pages/package/originpackage/create/index.jsx";
import { OriginPackagePage } from "./pages/package/originpackage/Index.jsx";
import { OriginPackageTransportationCreatePage } from "./pages/package/originpackagetransportation/create/index.jsx";
import { OriginPackageTransportationPage } from "./pages/package/originpackagetransportation/Index.jsx";
import { SalesPage } from "./pages/sales/Index.jsx";
import { CreateRoleManagementPage } from "./pages/usermanagement/group/create";
import { RoleManagementPage } from "./pages/usermanagement/group/Index.jsx";
import { CreateUserManagementPage } from "./pages/usermanagement/user/create";
import { UserManagementPage } from "./pages/usermanagement/user/Index.jsx";
import { CreateWarehousePage } from "./pages/warehouse/create";
import { WarehousePage } from "./pages/warehouse/Index.jsx";

// Components
let PrivateRoute = ({ children, requiredPermissions }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token"); // Retrieve token from localStorage

  const { permissions, isSuperuser, groups } = useMemo(() => {
    if (!token) return { permissions: [], isSuperuser: false, groups: [] };
    try {
      const decoded = jwtDecode(token);
      return {
        permissions: Array.isArray(decoded?.permission) ? decoded.permission : [],
        isSuperuser: Boolean(decoded?.is_superuser), // แปลงเป็น Boolean เพื่อป้องกันข้อผิดพลาด
        groups: Array.isArray(decoded?.group_id) ? decoded.group_id : [],
      };
    } catch (err) {
      console.error("[PrivateRoute] Invalid JWT: ", err);
      return { permissions: [], isSuperuser: false, groups: [] };
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      console.log("No token found, redirecting to login...");
      navigate("/");
    }
    // ใช้ ?? [] เพื่อป้องกัน error ถ้า permissions เป็น undefined
    const userPermissions = permissions ?? [];
    if (!isSuperuser && requiredPermissions && requiredPermissions.length > 0) {
      /** โมดูล company: DestinationWarehouse เข้าได้เหมือน superadmin */
      const companyListBypass =
        requiredPermissions.includes(Permissions.Company.VIEW) && groups.includes(AuthGroup.DestinationWarehouse);
      /** โมดูล customer: CompanySale เข้าได้เพื่อค้นหาข้อมูลลูกค้าของ sale ตัวเอง */
      const customerCompanySaleBypass =
        requiredPermissions.includes(Permissions.Customer.VIEW) && groups.includes(AuthGroup.CompanySale);
      if (companyListBypass || customerCompanySaleBypass) {
        return;
      }
      const hasPermission = requiredPermissions.some((perm) => userPermissions.includes(perm));
      if (!hasPermission) {
        console.log("Permission denied, redirecting...");
        navigate("/logout");
      }
    }
  }, [token, permissions, isSuperuser, groups, navigate, requiredPermissions]);

  // }, [isLoading, userData, signIn])
  return <AdminLayout>{children}</AdminLayout>;
};

export const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    element: <Login />,
  },
  {
    id: "login",
    path: "/login",
    element: <Login />,
  },
  {
    // id: 'root',
    // path: '/',
    children: [
      {
        path: "/home",
        something: true,
        element: (
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        ),
      },
      {
        path: "/company",
        // something: true,
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Company.VIEW]}>
            <CompanyPage />
          </PrivateRoute>
        ),
      },
      {
        path: "/customer",
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Customer.VIEW]}>
            <CustomerPage />
          </PrivateRoute>
        ),
      },
      {
        path: "/company/create",
        element: (
          <PrivateRoute>
            <CompanyCreatePage requiredPermissions={[Permissions.Company.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/customer/create",
        element: (
          <PrivateRoute>
            <CustomerCreatePage requiredPermissions={[Permissions.Customer.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage",
        element: (
          <PrivateRoute>
            <OriginPackagePage requiredPermissions={[Permissions.OriginPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage/create",
        element: (
          <PrivateRoute>
            <OriginPackageCreatePage requiredPermissions={[Permissions.OriginPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage/create/:id/:edit",
        element: (
          <PrivateRoute>
            <OriginPackageCreatePage requiredPermissions={[Permissions.OriginPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackagetransportation",
        element: (
          <PrivateRoute>
            <OriginPackageTransportationPage requiredPermissions={[Permissions.OriginPackageTransportation.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackagetransportation/create",
        element: (
          <PrivateRoute>
            <OriginPackageTransportationCreatePage
              requiredPermissions={[Permissions.OriginPackageTransportation.ADD]}
            />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackage",
        element: (
          <PrivateRoute>
            <DestinationPackagePage requiredPermissions={[Permissions.DestinationPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackage/create",
        element: (
          <PrivateRoute>
            <DestinationPackageCreatePage requiredPermissions={[Permissions.DestinationPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationdelivery",
        element: (
          <PrivateRoute>
            <DestinationDeliveryPage requiredPermissions={[Permissions.DestinationPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationdelivery/create",
        element: (
          <PrivateRoute>
            <DestinationDeliveryCreatePage requiredPermissions={[Permissions.DestinationPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackagedelivery",
        element: (
          <PrivateRoute>
            <DestinationPackageDeliveryPage requiredPermissions={[Permissions.DestinationPackageDelivery.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackagedelivery/create",
        element: (
          <PrivateRoute>
            <DestinationPackageDeliveryCreatePage requiredPermissions={[Permissions.DestinationPackageDelivery.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/companybill",
        element: (
          <PrivateRoute>
            <CompanyBillPage requiredPermissions={[Permissions.CompanyBill.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/companybill/create",
        element: (
          <PrivateRoute>
            <CompanyBillCreatePage requiredPermissions={[Permissions.CompanyBill.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/customerbill",
        element: (
          <PrivateRoute>
            <CustomerBilPage requiredPermissions={[Permissions.CustomerBill.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/customerbill/create",
        element: (
          <PrivateRoute>
            <CustomerBilCreatePage requiredPermissions={[Permissions.CustomerBill.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/sales",
        element: (
          <PrivateRoute>
            <SalesPage requiredPermissions={[Permissions.Sale.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/warehouses",
        element: (
          <PrivateRoute>
            <WarehousePage requiredPermissions={[Permissions.Warehouse.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/warehouses/create",
        element: (
          <PrivateRoute>
            <CreateWarehousePage requiredPermissions={[Permissions.Warehouse.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/user",
        element: (
          <PrivateRoute>
            <UserManagementPage requiredPermissions={[Permissions.User.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/user/create",
        element: (
          <PrivateRoute>
            <CreateUserManagementPage requiredPermissions={[Permissions.User.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/group",
        element: (
          <PrivateRoute>
            <RoleManagementPage requiredPermissions={[Permissions.Group.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/group/create",
        element: (
          <PrivateRoute>
            <CreateRoleManagementPage requiredPermissions={[Permissions.Group.ADD]} />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: "/callback",
    children: [
      {
        path: "/callback/success",
        element: <Success />,
      },
      {
        path: "/callback/logout",
        element: <LogoutSuccess />,
      },
    ],
    // element: <Success />,
  },
  {
    path: "/logout",
    element: <Logout />,
  },
]);
